/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, type MutableRefObject, useRef } from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { breakpoints } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import VisibilityTracking from 'bundles/page-config-common/components/shared/VisibilityTracking';
import PageConfigComponentMetadataProvider from 'bundles/page-config-common/providers/PageConfigComponentMetadataProvider';
import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

type Props = {
  pageConfigComponents: Array<PageConfigComponent | null | undefined>;
  renderComponent: (
    pageConfigComponent: PageConfigComponent,
    pageConfigComponents: Array<PageConfigComponent | null | undefined>,
    index: number,
    trackableContentRef?: MutableRefObject<HTMLDivElement | null>
  ) => JSX.Element | null;
  renderHorizontally?: boolean;
};

const styles = {
  collectionsGrid: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr;
    gap: calc(var(--cds-spacing-150) / 2 * 3);
    width: 100%;

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(3, minmax(343px, 1fr));
      width: auto;
    }
  `,
};

type PageConfigComponentRendererElementProps = Props & {
  pageConfigComponent: PageConfigComponent;
  index: number;
};

const PageConfigComponentRendererElement = (props: PageConfigComponentRendererElementProps) => {
  const { pageConfigComponent, index, pageConfigComponents, renderComponent } = props;

  const trackableContentRef = useRef<HTMLDivElement | null>(null);

  return (
    <PageConfigComponentMetadataProvider
      id={pageConfigComponent.id}
      index={index}
      name={pageConfigComponent.__typename}
      recommender={{
        context: pageConfigComponent.rankingContext ?? undefined,
        hash: pageConfigComponent.recommenderConfigurationHash ?? '',
      }}
    >
      <VisibilityTracking
        pageConfigComponent={pageConfigComponent}
        index={index}
        trackableContentRef={trackableContentRef}
      >
        <ErrorBoundaryWithTagging componentName={pageConfigComponent.__typename}>
          {renderComponent(pageConfigComponent, pageConfigComponents, index, trackableContentRef)}
        </ErrorBoundaryWithTagging>
      </VisibilityTracking>
    </PageConfigComponentMetadataProvider>
  );
};

const PageConfigComponentRendererWrapper = (props: Props) => {
  const { pageConfigComponents, renderComponent, renderHorizontally } = props;
  const WrappingComponent = renderHorizontally ? OverflowCarousel : Fragment;

  return (
    <WrappingComponent {...(renderHorizontally ? { css: styles.collectionsGrid } : {})}>
      {pageConfigComponents.map((pageConfigComponent, index) => {
        if (!pageConfigComponent) {
          return null;
        }

        return (
          <PageConfigComponentRendererElement
            key={pageConfigComponent.id}
            pageConfigComponents={pageConfigComponents}
            renderComponent={renderComponent}
            pageConfigComponent={pageConfigComponent}
            index={index}
          />
        );
      })}
    </WrappingComponent>
  );
};

export default PageConfigComponentRendererWrapper;
